'use client';

import {
  Box,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Divider,
  Flex,
  Link,
  List,
  ListIcon,
  ListItem,
  SimpleGrid,
  Tag,
  Text,
} from '@chakra-ui/react';
import { useState } from 'react';
import slugify from 'react-slugify';

import ButtonCustom from '@/app/_components/button/ButtonCustom';
import { ReactComponent as PromotionTag } from '@/app/_icons/promotion-tag.svg';
import { ReactComponent as TickIcon } from '@/app/_icons/tick-icon.svg';
import { useFloatingCTA } from '@/hooks/floating-cta.hook';
import { getImageLink } from '@/services/link.service';
import { NewCarListing } from '@/types/new-cars/new-cars.types';

import CustomImage from '../custom-image/CustomImage';
import LinkCustom from '../link/LinkCustom';

import Styles from './FleetRangeCard.module.scss';

const FleetRangeCard = ({ fleetCar }: { fleetCar: NewCarListing }) => {
  const [isHover, setHover] = useState(false);
  const { openLeadForm } = useFloatingCTA();

  return (
    <Card
      className={Styles.vehicleCard}
      maxW={{ base: '100%', sm: '400px' }}
      width={'100%'}
      shadow={0}
      borderRadius={0}
      padding={0}
      marginX={'auto'}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <CardHeader
        className={Styles.cardHeader}
        display={'flex'}
        flexDirection={'row'}
      >
        <Box className={Styles.carInfo} maxW={'210'} width={'100%'}>
          <Box className={Styles.modelName}>{fleetCar?.main_title}</Box>
          <Text className={Styles.description}>
            {fleetCar?.intro_subheading}
          </Text>
        </Box>
        <Box className={Styles.carPriceInfo} marginLeft={'auto'}>
          <Flex direction={'column'} gap={1}>
            {fleetCar?.isPromotional && (
              <Tag borderRadius={0} className={Styles.promotionTag}>
                <PromotionTag />
                <span>Promotion</span>
              </Tag>
            )}
          </Flex>
        </Box>
      </CardHeader>
      <CardBody pt={0} pb={2}>
        <Link className={Styles.carLink}>
          {isHover ? (
            <CustomImage
              className={Styles.carImage}
              imageSrc={
                getImageLink(fleetCar?.image_no_background_left_angle) ?? ''
              }
              imageKeys={{
                desktopKey: 'image-322-193',
                mobileKey: 'image-278-172',
              }}
              alt={fleetCar?.image_alt_tag}
              width={322}
              height={193}
            />
          ) : (
            <CustomImage
              className={Styles.carImage}
              imageSrc={getImageLink(fleetCar?.image_no_background_side) ?? ''}
              imageKeys={{
                desktopKey: 'image-322-193',
                mobileKey: 'image-278-172',
              }}
              alt={fleetCar?.image_alt_tag}
              width={322}
              height={193}
            />
          )}
        </Link>
        <Flex
          className={Styles.carDetailsWrapper}
          direction={'row'}
          justifyContent={'center'}
          width={'100%'}
          paddingY={4}
        >
          <Box className={Styles.carDetails} textAlign={'center'}>
            <Text className={Styles.carLabel}>Fuel Type</Text>
            <Text className={Styles.carUnit}>
              <strong>{fleetCar?.lowestSpecfuelType}</strong>
            </Text>
          </Box>
          <Divider
            orientation="vertical"
            borderLeftWidth={'1px'}
            marginX={{ base: 2, sm: 3 }}
            opacity={1}
            className="borderLeft"
          />
          <Box className={Styles.carDetails} textAlign={'center'}>
            <Text className={Styles.carLabel}>Fuel Consumption</Text>
            <Text className={Styles.carUnit}>
              <strong>{fleetCar?.images_fuel_consumption}</strong>
              <span>L/100km</span>
            </Text>
          </Box>
          <Divider
            orientation="vertical"
            borderLeftWidth={'1px'}
            marginX={{ base: 2, sm: 3 }}
            opacity={1}
            className="borderLeft"
          />
          <Box className={Styles.carDetails} textAlign={'center'}>
            <Text className={Styles.carLabel}>
              {fleetCar?.co2_emissions_heading}
            </Text>
            <Text className={Styles.carUnit}>
              <strong>{fleetCar?.co2_emissions}</strong>
            </Text>
          </Box>
        </Flex>
        <List spacing={3} className={Styles.features} paddingTop={2}>
          {fleetCar?.fleet_features?.map((feature) => (
            <ListItem className={Styles.feature} key={feature?.feature}>
              <ListIcon as={TickIcon} />
              <span>{feature?.feature}</span>
            </ListItem>
          ))}
        </List>
      </CardBody>
      <CardFooter flexDir={'column'} className={Styles.cardFooter}>
        <SimpleGrid
          columns={[1]}
          spacing={[4]}
          mt={{ base: 6, md: 0 }}
          mb={{ base: 4, md: 0 }}
        >
          <ButtonCustom
            btnText="Enquire Now"
            className="btnOutline"
            moduleClass={Styles.ctaBtn}
            onClick={() => openLeadForm('fleet', 'Fleet', {
              type: 'new-car',
              newCar: {
                id: fleetCar?.id,
                main_title: fleetCar?.main_title,              
              }
            })}
          />
          <LinkCustom
            className=""
            moduleClass={Styles.ctaBtn}
            href={`/new-cars/${slugify(fleetCar?.main_title)}`}
          >{`Explore ${fleetCar?.main_title}`}</LinkCustom>
        </SimpleGrid>
      </CardFooter>
    </Card>
  );
};

export default FleetRangeCard;
